import React, { createContext, useState, useEffect } from 'react';
// @ts-ignore - configured in vite.config.ts via alias
import { authService } from '@/services/authService';

interface User {
    id: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    role: string;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
}

interface AuthContextType extends AuthState {
    setAuthState: React.Dispatch<React.SetStateAction<AuthState>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        token: localStorage.getItem('jwt_token'),
        isAuthenticated: false,
        isLoading: true,
    });

    useEffect(() => {
        const initAuth = async () => {
            // Check for demo token first to allow dev bypass to persist
            const demoMode = localStorage.getItem('demo_mode') === 'true';
            const token = localStorage.getItem('jwt_token');

            if (demoMode && token) {
                // Re-initialize demo state if it was set
                // Ideally we should call a service to verify the token, but for now we follow the existing pattern
                setAuthState(prev => ({
                    ...prev,
                    isLoading: false,
                    isAuthenticated: true,
                    // Note: We don't have the full user object here without re-running demoLogin or storing it
                    // For now, let the useAuth/authService handle re-syncing if needed
                }));
            }

            try {
                const session = await authService.getSession();
                if (session?.user) {
                    const user = {
                        id: session.user.id,
                        email: session.user.email || '',
                        role: session.user.app_metadata?.role || 'user',
                        username: session.user.user_metadata?.first_name || '',
                        firstName: session.user.user_metadata?.first_name || '',
                        lastName: session.user.user_metadata?.last_name || '',
                    };
                    setAuthState({
                        user,
                        token: session.access_token,
                        isAuthenticated: true,
                        isLoading: false,
                    });
                    localStorage.setItem('jwt_token', session.access_token);
                } else if (!demoMode) {
                    setAuthState(prev => ({ ...prev, isLoading: false, isAuthenticated: false, user: null }));
                }
            } catch (error) {
                console.error("Auth init failed", error);
                if (!demoMode) {
                    setAuthState(prev => ({ ...prev, isLoading: false }));
                }
            }
        };

        initAuth();

        const { data: { subscription } } = authService.onAuthStateChange((event: any, session: any) => {
            if (session?.user) {
                const user = {
                    id: session.user.id,
                    email: session.user.email || '',
                    username: session.user.user_metadata?.first_name || '',
                    firstName: session.user.user_metadata?.first_name || '',
                    lastName: session.user.user_metadata?.last_name || '',
                    role: session.user.app_metadata?.role || 'user',
                };
                setAuthState({
                    user,
                    token: session.access_token,
                    isAuthenticated: true,
                    isLoading: false,
                });
                localStorage.setItem('jwt_token', session.access_token);
            } else if (event === 'SIGNED_OUT') {
                setAuthState({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
                localStorage.removeItem('jwt_token');
                localStorage.removeItem('demo_mode');
                localStorage.removeItem('isAuthenticated');
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    return (
        <AuthContext.Provider value={{ ...authState, setAuthState }}>
            {children}
        </AuthContext.Provider>
    );
};
